import type { LocationModel } from '@/entities/location';
import { MAP_REPORTS, type ReportValues } from '@/features/map-layers';

import type { ClusterNameResolver, ClusterSource, MapCluster, MapGroupBy } from '../model/types';

const GROUP_KEYS = {
  country: 'countryId',
  region: 'regionId',
  subregion: 'subregionId',
  locationGroup: 'siteId'
} as const satisfies Record<Exclude<MapGroupBy, 'location'>, keyof LocationModel>;

const emptyValues = (): ReportValues => ({ RPU: 0, SOH: 0, RIC: 0, REL: 0 });

/**
 * Collapses locations into the markers the map draws, following legacy RITA
 * (`mapping.js:331-408`): one cluster per grouping key, its coordinate the plain
 * average of its members', its slices the sums of theirs. Clusters that add up to
 * nothing are dropped — legacy skips them too, so an empty region shows no marker.
 */
export function buildClusters(
  sources: ClusterSource[],
  groupBy: MapGroupBy,
  resolveName: ClusterNameResolver
): MapCluster[] {
  const clusters = new Map<string, MapCluster>();

  for (const { location, values } of sources) {
    const id = groupBy === 'location' ? location.id : location[GROUP_KEYS[groupBy]];
    if (!id) continue;

    let cluster = clusters.get(id);
    if (!cluster) {
      cluster = {
        id,
        name: groupBy === 'location' ? location.name : resolveName(id),
        lat: 0,
        lng: 0,
        values: emptyValues(),
        total: 0,
        locations: []
      };
      clusters.set(id, cluster);
    }

    for (const report of MAP_REPORTS) {
      cluster.values[report] += Math.abs(values[report]);
    }
    cluster.lat += location.lat;
    cluster.lng += location.lng;
    cluster.locations.push(location);
  }

  return [...clusters.values()]
    .map((cluster) => ({
      ...cluster,
      lat: cluster.lat / cluster.locations.length,
      lng: cluster.lng / cluster.locations.length,
      total: MAP_REPORTS.reduce((sum, report) => sum + cluster.values[report], 0)
    }))
    .filter((cluster) => cluster.total > 0);
}
